import { CREATURE_BY_ID, PALETTE, paletteColor, PING, PING_LABELS, pingKindForCreature, type PingKind } from "@eldrun/content";
import type { MarkEvent } from "@eldrun/protocol";
import { Vector3, type Camera } from "three";
import { h } from "../ui/kit/dom.ts";

export interface Ray {
  origin: { x: number; y: number; z: number };
  dir: { x: number; y: number; z: number };
}

export interface PingTarget {
  x: number;
  y: number;
  z: number;
  kind: PingKind;
  /** Criatura mirada (o servidor confere se está no ponto). */
  target?: string;
}

/** Primeiro ponto em que o raio toca o terreno até `max` m (passo de 0,5 m, refinado), ou undefined. */
export function rayGround(ray: Ray, height: (x: number, z: number) => number, max: number = PING.range): number | undefined {
  const at = (t: number) => ray.origin.y + ray.dir.y * t - height(ray.origin.x + ray.dir.x * t, ray.origin.z + ray.dir.z * t);
  for (let t = 0.5; t <= max; t += 0.5) {
    if (at(t) > 0) continue;
    let lo = t - 0.5;
    let hi = t;
    for (let i = 0; i < 8; i++) {
      const mid = (lo + hi) / 2;
      if (at(mid) <= 0) hi = mid;
      else lo = mid;
    }
    return hi;
  }
  return undefined;
}

/**
 * Ping contextual (doc 05 e 06): criatura na linha da mira antes do chão → "perigo" (hostil) ou
 * "criatura" (fauna); senão, no ponto do chão, item caído (1,5 m) → "item", recurso (2,5 m) →
 * "recurso", e o resto é "aqui". Sem chão nem criatura em 300 m, não há ping.
 */
export function inferPing(
  ray: Ray,
  height: (x: number, z: number) => number,
  creatures: Iterable<{ id: string; type: string; x: number; y: number; z: number; hp: number }>,
  dropNear: (x: number, z: number, r: number) => boolean,
  resourceNear: (x: number, z: number, r: number) => boolean,
): PingTarget | undefined {
  const ground = rayGround(ray, height);
  let best: { t: number; id: string; type: string; x: number; y: number; z: number } | undefined;
  for (const c of creatures) {
    if (c.hp <= 0) continue;
    const def = CREATURE_BY_ID.get(c.type);
    const r = (def?.radius ?? 0.5) + 0.6;
    const mid = c.y + (def?.height ?? 1) / 2;
    // Distância do raio ao centro do corpo.
    const dx = c.x - ray.origin.x;
    const dy = mid - ray.origin.y;
    const dz = c.z - ray.origin.z;
    const t = dx * ray.dir.x + dy * ray.dir.y + dz * ray.dir.z;
    if (t <= 0 || t > PING.range || (ground !== undefined && t > ground + r)) continue;
    const off = Math.hypot(dx - ray.dir.x * t, dy - ray.dir.y * t, dz - ray.dir.z * t);
    if (off <= r + (def?.height ?? 1) / 2 && (!best || t < best.t)) best = { t, id: c.id, type: c.type, x: c.x, y: c.y, z: c.z };
  }
  if (best) {
    const archetype = CREATURE_BY_ID.get(best.type)?.archetype ?? "neutra";
    return { x: best.x, y: best.y, z: best.z, kind: pingKindForCreature(archetype), target: best.id };
  }
  if (ground === undefined) return undefined;
  const x = ray.origin.x + ray.dir.x * ground;
  const z = ray.origin.z + ray.dir.z * ground;
  const y = height(x, z);
  const kind: PingKind = dropNear(x, z, 1.5) ? "item" : resourceNear(x, z, 2.5) ? "recurso" : "aqui";
  return { x, y, z, kind };
}

interface ActivePing {
  event: MarkEvent;
  name: string;
  until: number;
  el: HTMLElement;
  /** Hex da paleta de quem pingou (o neutro, se ele saiu); a cor na tela passa pelo modo daltônico. */
  hex: string;
}

const EDGE = 28;
const v = new Vector3();

/**
 * Onde desenhar um ponto do mundo na tela: projetado, ou (fora da vista ou atrás da câmera) preso
 * à borda na direção dele a partir do centro, com o ângulo da seta.
 */
export function screenPoint(p: { x: number; y: number; z: number }, camera: Camera, width: number, height: number): { x: number; y: number; off: boolean; angle: number } {
  const cx = width / 2;
  const cy = height / 2;
  // Coordenadas da câmera: à frente é z negativo.
  v.set(p.x, p.y, p.z).applyMatrix4(camera.matrixWorldInverse);
  let dx: number;
  let dy: number;
  let inside = false;
  let sx = cx;
  let sy = cy;
  if (v.z < -0.05) {
    v.set(p.x, p.y, p.z).project(camera);
    sx = ((v.x + 1) / 2) * width;
    sy = ((1 - v.y) / 2) * height;
    inside = sx >= EDGE && sx <= width - EDGE && sy >= EDGE && sy <= height - EDGE;
    dx = sx - cx;
    dy = sy - cy;
  } else {
    // Atrás ou no plano da câmera: a direção vem das coordenadas da câmera; bem atrás, para baixo.
    dx = v.x;
    dy = -v.y;
    if (Math.hypot(dx, dy) < 1e-6) {
      dx = 0;
      dy = 1;
    }
  }
  const angle = Math.atan2(dy, dx);
  if (inside) return { x: sx, y: sy, off: false, angle };
  const k = Math.min((cx - EDGE) / Math.max(1e-6, Math.abs(dx)), (cy - EDGE) / Math.max(1e-6, Math.abs(dy)));
  return { x: cx + dx * k, y: cy + dy * k, off: true, angle };
}

/**
 * Pings no mundo (doc 06): marcador HTML com símbolo, tipo, autor e distância; fora da vista, preso
 * à borda da tela com uma seta. Cada um dura 6 s; no máximo 3 por autor (o novo tira o mais velho).
 */
export class PingMarkers {
  readonly element: HTMLElement;
  private readonly active: ActivePing[] = [];
  /** Modo daltônico (F75: a variante da paleta na cor de quem pingou); o símbolo e o texto vêm sempre. */
  private palette = false;

  get colorblind(): boolean {
    return this.palette;
  }

  /** Troca a paleta também nos pings que já estão na tela. */
  set colorblind(on: boolean) {
    this.palette = on;
    for (const p of this.active) p.el.style.setProperty("--cor", paletteColor(p.hex, on));
  }

  constructor(parent: HTMLElement) {
    this.element = h("div", { id: "pings", "aria-live": "polite" });
    parent.appendChild(this.element);
  }

  /** `ownerHex` (F63/F75): o hex da paleta de quem pingou; a cor é de quem, o símbolo diz o tipo. */
  add(event: MarkEvent, name: string, now: number, ownerHex: string = PALETTE.neutro.hex): void {
    const mine = this.active.filter((p) => p.event.by === event.by);
    if (mine.length >= PING.maxActive) this.drop(mine[0]!);
    const look = PING_LABELS[event.kind];
    const el = h(
      "div",
      { class: `ping ${event.kind}`, role: "img", "aria-label": `${name}: ${look.label}` },
      h("span", { class: "simbolo", "aria-hidden": "true" }, look.symbol),
      h("span", { class: "rotulo" }, ""),
      h("span", { class: "seta", "aria-hidden": "true" }, "➤"),
    );
    el.style.setProperty("--cor", paletteColor(ownerHex, this.palette));
    this.element.appendChild(el);
    this.active.push({ event, name, until: now + event.ms, el, hex: ownerHex });
  }

  /** Pings vivos (o mapa também desenha). */
  list(now: number): readonly { event: MarkEvent; name: string; hex: string }[] {
    return this.active.filter((p) => p.until > now);
  }

  get count(): number {
    return this.active.length;
  }

  update(camera: Camera, width: number, height: number, now: number): void {
    for (const p of [...this.active]) {
      if (now >= p.until) {
        this.drop(p);
        continue;
      }
      const { x, y, z, kind } = p.event;
      const { x: sx, y: sy, off, angle } = screenPoint({ x, y: y + 1.2, z }, camera, width, height);
      if (off) p.el.style.setProperty("--giro", `${angle}rad`);
      p.el.classList.toggle("borda", off);
      const dist = Math.round(Math.hypot(x - camera.position.x, y - camera.position.y, z - camera.position.z));
      const label = `${p.name} · ${PING_LABELS[kind].label} · ${dist} m`;
      const rotulo = p.el.children[1] as HTMLElement;
      if (rotulo.textContent !== label) rotulo.textContent = label;
      p.el.style.transform = `translate(${sx.toFixed(1)}px, ${sy.toFixed(1)}px)`;
    }
  }

  private drop(p: ActivePing): void {
    p.el.remove();
    this.active.splice(this.active.indexOf(p), 1);
  }

  remove(): void {
    this.element.remove();
  }
}
